import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '../store';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { AlertTriangle, Copy, MessageCircle, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PaymentSuccessAnimation from '../components/PaymentSuccessAnimation';

export default function RenovarPage() {
  const { api, user } = useAuthStore();
  const { token } = useParams(); // /renovar/:token (público) — id do revendedor
  const publico = !!token;
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => { checar(); }, []);

  const checar = async () => {
    try {
      if (publico) {
        const r = await axios.get(`${BACKEND}/api/public/renovar/${token}`);
        setStatus({ ...r.data, aplicavel: true });
      } else {
        const r = await api.get('/my-subscription/status');
        setStatus(r.data);
      }
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  };

  const handleRenew = async () => {
    try {
      const response = publico
        ? await axios.post(`${BACKEND}/api/public/renovar/${token}/pix`)
        : await api.post(`/renew/${user.id}`);
      setPaymentData(response.data);
      startPaymentPolling(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
    }
  };

  const [gerandoCartao, setGerandoCartao] = useState(false);
  const handleRenewCard = async () => {
    setGerandoCartao(true);
    try {
      const response = publico
        ? await axios.post(`${BACKEND}/api/public/renovar/${token}/pix`, { metodo: 'cartao' })
        : await api.post(`/renew/${user.id}`, { metodo: 'cartao' });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Não foi possível gerar o link de cartão');
        setGerandoCartao(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar cartão');
      setGerandoCartao(false);
    }
  };

  const startPaymentPolling = (txId) => {
    setCheckingPayment(true);
    const iv = setInterval(async () => {
      try {
        const r = publico
          ? await axios.get(`${BACKEND}/api/public/transaction/${txId}`)
          : await api.get(`/transactions/${txId}`);
        if (r.data.status === 'approved') {
          clearInterval(iv);
          setCheckingPayment(false);
          setShowSuccess(true);
          setTimeout(() => { window.location.href = publico ? '/login' : '/'; }, 2500);
        }
      } catch {}
    }, 4000);
    setTimeout(() => clearInterval(iv), 15 * 60 * 1000);
  };

  const copiarPix = (text) => { navigator.clipboard.writeText(text); toast.success('Código PIX copiado!'); };

  const abrirContato = () => {
    const num = (status?.contato_upline || '').replace(/\D/g, '');
    if (!num) { toast.error('Contato do seu revendedor não disponível'); return; }
    const msg = encodeURIComponent('Olá! Quero renovar minha assinatura.');
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
  };

  if (showSuccess) return <PaymentSuccessAnimation onComplete={() => setShowSuccess(false)} />;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!status || !status.aplicavel) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full glass-card">
        <CardContent className="p-8 text-center space-y-3">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
          <p className="text-foreground font-medium">Sua assinatura está em dia!</p>
          <Button variant="outline" onClick={() => window.location.href = publico ? '/login' : '/'}>Voltar ao painel</Button>
        </CardContent>
      </Card>
    </div>
  );

  const dias = status.dias;
  const titulo = dias < 0 ? 'Sua assinatura venceu' : dias === 0 ? 'Sua assinatura vence hoje' : `Sua assinatura vence em ${dias} dia${dias > 1 ? 's' : ''}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted/30">
      <Card className="max-w-md w-full glass-card">
        <CardContent className="p-6 space-y-5">
          <div className="text-center space-y-2">
            <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${dias < 0 ? 'bg-red-500/15 text-red-500' : 'bg-amber-500/15 text-amber-500'}`}>
              {dias < 0 ? <AlertTriangle className="w-7 h-7" /> : <Clock className="w-7 h-7" />}
            </div>
            <h1 className="text-xl font-bold text-foreground">{titulo}</h1>
            <p className="text-sm text-muted-foreground">Olá, {status?.username || user?.username}!</p>
          </div>

          {!paymentData ? (
            <div className="space-y-4">
              {status.pode_renovar ? (
                <>
                  <div className="p-4 rounded-xl bg-muted/40 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Valor da renovação</p>
                    <p className="text-3xl font-bold text-foreground mt-1">R$ {Number(status.valor || 0).toFixed(2).replace('.', ',')}</p>
                    <p className="text-xs text-muted-foreground mt-1">+1 mês de acesso</p>
                  </div>
                  {status.checkout_unificado ? (
                    <Button onClick={handleRenewCard} className="w-full" size="lg" disabled={gerandoCartao}>
                      {gerandoCartao ? 'Abrindo pagamento...' : 'Renovar Agora (PIX ou Cartão)'}
                    </Button>
                  ) : (
                    <>
                      <Button onClick={handleRenew} className="w-full" size="lg" disabled={gerandoCartao}>
                        {status.suporta_cartao ? 'Pagar com PIX' : 'Gerar PIX e renovar'}
                      </Button>
                      {status.suporta_cartao && (
                        <Button onClick={handleRenewCard} variant="outline" className="w-full border-primary/40" size="lg" disabled={gerandoCartao}>
                          {gerandoCartao ? 'Abrindo cartão...' : '💳 Pagar com cartão'}
                        </Button>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="space-y-3 text-center">
                  <p className="text-sm text-muted-foreground">
                    {status.upline_tem_gateway === false
                      ? 'Seu revendedor não tem pagamento automático no momento.'
                      : 'Seu revendedor está temporariamente sem créditos.'}
                    {' '}Fale com ele para renovar:
                  </p>
                  <Button onClick={abrirContato} className="w-full bg-green-600 hover:bg-green-700" size="lg" disabled={!status.contato_upline}>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {status.contato_upline ? `Falar com ${status.upline_nome || 'meu revendedor'}` : 'Contato indisponível'}
                  </Button>
                </div>
              )}
              <button onClick={() => window.location.href = publico ? '/login' : '/'} className="w-full text-xs text-muted-foreground hover:text-foreground">Voltar ao painel</button>
            </div>
          ) : (
            <div className="space-y-4">
              {checkingPayment && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto mb-1" />
                  <p className="text-sm text-blue-500">Aguardando confirmação do pagamento...</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Escaneie o QR Code:</p>
                <img
                  src={paymentData.qr_code ? (paymentData.qr_code.startsWith('http') ? paymentData.qr_code : paymentData.qr_code.startsWith('data:') ? paymentData.qr_code : `data:image/png;base64,${paymentData.qr_code}`) : (paymentData.qr_code_url || (paymentData.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentData.qr_code_text)}` : ''))}
                  alt="QR Code PIX" className="mx-auto w-56 h-56 border rounded-lg bg-white p-2" />
              </div>
              <div className="flex gap-2">
                <input type="text" value={paymentData.qr_code_text} readOnly className="flex-1 p-2 text-xs border rounded bg-muted font-mono" />
                <Button size="sm" onClick={() => copiarPix(paymentData.qr_code_text)}><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-yellow-600 text-center">⏱️ Após o pagamento, a confirmação pode levar até 2 minutos.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
